import { useInfiniteQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { DatasetOrdering, DatasetSorting } from "../../../../api/trading"
import { useApi } from "../../../hooks/useApi"
import { DatasetSource, TradingDataset } from "../Dataset/types"

interface IInfiniteDatasets {
  pageParam?: string
  codeStartswithParam?: string
  nameParam?: string
  longNameParam?: string
  sourceParam?: DatasetSource
  currencyCodeParam?: string[]
  countryCodeParam?: string[]
  regionIdParam?: number
  subRegionIdParam?: number
  sectorsParam?: number[]
  withPaginationParam?: boolean
  orderBy?: DatasetOrdering
  sortDirection?: DatasetSorting
  enabled?: boolean
}

// Define the response type for better type safety
interface DatasetResponse {
  data: TradingDataset[]
  next_cursor: string | null
}

const useInfiniteDatasets = ({
  codeStartswithParam,
  nameParam,
  longNameParam,
  sourceParam,
  currencyCodeParam,
  countryCodeParam,
  regionIdParam,
  subRegionIdParam,
  sectorsParam,
  withPaginationParam = true,
  orderBy = DatasetOrdering.NAME,
  sortDirection = DatasetSorting.ASC,
  enabled = true,
}: IInfiniteDatasets) => {
  const { scientApi } = useApi()

  const { data, error, fetchNextPage, hasNextPage, status, isFetching, isFetchingNextPage } =
    useInfiniteQuery<DatasetResponse>(
      [
        "trading_datasets",
        { orderBy, sortDirection },
        {
          code_startswith: codeStartswithParam,
          name: nameParam,
          long_name: longNameParam,
          source: sourceParam,
          currency_code: currencyCodeParam,
          country_code: countryCodeParam,
          region_id: regionIdParam,
          sub_region_id: subRegionIdParam,
          sectors: sectorsParam,
          with_pagination: withPaginationParam,
        },
      ],
      ({ pageParam }) =>
        scientApi.trading.getTradingDatasets({
          pageParam,
          codeStartswithParam,
          nameParam,
          longNameParam,
          sourceParam,
          currencyCodeParam,
          countryCodeParam,
          regionIdParam,
          subRegionIdParam,
          sectorsParam,
          withPaginationParam,
          orderBy,
          sortDirection,
        }),
      {
        getNextPageParam: (lastPage, pages) => {
          if (lastPage.next_cursor) {
            return lastPage.next_cursor
          }
          return undefined
        },
        staleTime: Infinity,
        enabled: enabled || (withPaginationParam && (codeStartswithParam?.length ?? 0) > 2),
        // https://github.com/TanStack/query/issues/3065
        // TODO: once v5 is out, allow typing on select for infinite data
        // @ts-ignore
        select: data => {
          let datasets: TradingDataset[] = []
          for (const page of data.pages) {
            datasets = [...datasets, ...page.data]
          }
          return datasets
        },
      },
    )

  return {
    // @ts-ignore
    datasets: data as TradingDataset[] | undefined,
    fetchNextPage,
    hasNextPage,
    error: error as AxiosError,
    status,
    isFetching,
    isFetchingNextPage,
  }
}

export default useInfiniteDatasets
